import React, { useState } from "react";
import { TrendingUp, Minus, TrendingDown, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import StarRating from "@/components/StarRating";
import api from "@/lib/api";
import { TRAITS } from "@/lib/traits";
import { toast } from "sonner";

const OPTIONS = [
  { key: "improved", label: "Improved", icon: TrendingUp, cls: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { key: "same", label: "Stayed the same", icon: Minus, cls: "border-slate-500 bg-slate-100 text-slate-800" },
  { key: "worse", label: "Gotten worse", icon: TrendingDown, cls: "border-rose-500 bg-rose-50 text-rose-800" },
];

export default function CheckinModal({ review, supervisorName, open, onOpenChange, onDone }) {
  const [direction, setDirection] = useState("");
  const [rerate, setRerate] = useState(false);
  const [traits, setTraits] = useState({});
  const [saving, setSaving] = useState(false);

  const allRated = TRAITS.every((t) => traits[t.key] > 0);

  const submit = async () => {
    if (!direction) return;
    if (rerate && !allRated) { toast.error("Rate all traits or turn off re-rating"); return; }
    setSaving(true);
    try {
      await api.post(`/reviews/${review.id}/checkins`, { direction, traits: rerate ? traits : null });
      toast.success("Check-in saved — thanks for keeping this current");
      onOpenChange(false);
      setDirection(""); setRerate(false); setTraits({});
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save check-in");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="checkin-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ fontWeight: 800 }}>
            <RefreshCw className="text-emerald-600" size={20} /> Still accurate?
          </DialogTitle>
          <DialogDescription>
            Since your review, has working under {supervisorName}…
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="checkin-options">
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              data-testid={`checkin-option-${o.key}`}
              onClick={() => setDirection(o.key)}
              className={`rounded-xl border-2 p-3 flex flex-col items-center gap-1.5 text-sm font-semibold transition-all ${
                direction === o.key ? o.cls : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              <o.icon size={20} /> {o.label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-600 mt-2">
          <input type="checkbox" data-testid="checkin-rerate-toggle" checked={rerate} onChange={(e) => setRerate(e.target.checked)} className="accent-emerald-600 h-4 w-4" />
          Re-rate the {TRAITS.length} traits (optional — updates the trends chart)
        </label>

        {rerate && (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="checkin-traits">
            {TRAITS.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2">
                <span className="text-sm text-slate-700">{t.label}</span>
                <StarRating testid={`checkin-trait-${t.testid}`} value={traits[t.key] || 0} onChange={(v) => setTraits((s) => ({ ...s, [t.key]: v }))} size={18} />
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 leading-relaxed">
          Check-ins are anonymous, shown only as a percentage once enough exist, and never reveal exact dates. Your original review stays unchanged.
        </p>

        <Button data-testid="checkin-submit" onClick={submit} disabled={!direction || saving} className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full">
          {saving ? "Saving..." : "Save check-in"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
